import React, { createContext, useContext, useEffect, useState } from "react";

import { useAuth } from "@/features/Auth/stores/auth-store";
import { SyncService } from "@/lib/storage/device-sync-service";
import { PullSyncService } from "@/lib/storage/pull-sync-service";
import { getKVStatus } from "@/lib/storage/kv-client";
import { SyncEvent } from "@/lib/storage/types";
import useCoachProfile from "@/app/coach/features/coach-profile/hooks/useCoachProfile.ts";

// Types for device information
export interface DeviceInfo {
  // Device capabilities
  isTouchDevice: boolean;
  isHoverSupported: boolean;

  // Screen information
  screenSize: {
    width: number;
    height: number;
  };
  orientation: "portrait" | "landscape";
  deviceType: "mobile" | "tablet" | "desktop";

  // Device identification
  deviceId: string;
  deviceName?: string; // Custom name set by user (e.g., "Dave's Mobile")
  userId: string; // Owner of the device

  // Feature detection
  hasCamera: boolean;
  hasGPS: boolean;
  connection: {
    type: string;
    speed: "slow" | "medium" | "fast";
  };
  lastSync?: number;

  // Platform/OS detection
  os: "ios" | "android" | "windows" | "macos" | "linux" | "unknown";
  browser: "safari" | "chrome" | "firefox" | "edge" | "unknown";

  // PWA status
  isPWAInstalled: boolean; // Is app running as installed PWA?
  canInstallPWA: boolean; // Can this browser/platform install PWAs?
}

interface DeviceContextType extends DeviceInfo {
  // Device management
  registerDevice: () => Promise<void>;
  unregisterDevice: () => Promise<void>;
  getRegisteredDevices: () => Promise<DeviceInfo[]>;
  removeDevice: (deviceId: string) => Promise<void>;
  updateDeviceName: (deviceId: string, deviceName: string) => Promise<void>;
  registeredDevices: DeviceInfo[];
  isCurrentDevice: (deviceId: string) => boolean;
  syncStatus: SyncState;
  refreshDeviceSyncData: () => Promise<void>;
  sync: () => Promise<void>;
  pullSync: () => Promise<void>;
}

export interface SyncState {
  isSyncing: boolean;
  error: string | null;
  pendingUpdates: SyncEvent[]; // Pending downloads (remote changes to pull)
  pendingUploads: number; // Pending uploads (local changes to push)
  status: "idle" | "ok" | "stale" | "unknown";
  requiresFullSync: boolean;
  pullProgress?: {
    current: number;
    total: number;
    message: string;
  };
  lastPullSync?: number;
}

const DeviceContext = createContext<DeviceContextType | undefined>(undefined);

const DEVICE_ID_KEY = "app_device_id";

export const getDeviceIdFromLocalStorage = (): string | null => {
  return localStorage.getItem(DEVICE_ID_KEY);
};

// Helper functions (keep existing ones)
const generateDeviceId = (): string => {
  const existingId = getDeviceIdFromLocalStorage();
  if (existingId) {
    return existingId;
  }

  const components = [
    navigator.userAgent,
    `${window.screen.width}x${window.screen.height}`,
    navigator.hardwareConcurrency,
    // @ts-expect-error - deviceMemory is not in all browsers
    navigator.deviceMemory,
    crypto.randomUUID(),
  ];

  const newId = btoa(components.join("|")).replace(/[/+=]/g, "").slice(0, 32);

  try {
    localStorage.setItem(DEVICE_ID_KEY, newId);
  } catch (error) {
    console.warn("Failed to store device ID in localStorage:", error);
  }
  return newId;
};

const detectDeviceType = (width: number): "mobile" | "tablet" | "desktop" => {
  if (width < 768) return "mobile";
  if (width < 1024) return "tablet";
  return "desktop";
};

interface NavigatorConnection {
  effectiveType?: string;
  addEventListener?: (
    type: string,
    listener: EventListenerOrEventListenerObject,
  ) => void;
  removeEventListener?: (
    type: string,
    listener: EventListenerOrEventListenerObject,
  ) => void;
}

const detectConnectionSpeed = (): "slow" | "medium" | "fast" => {
  const connection = (
    navigator as Navigator & { connection?: NavigatorConnection }
  ).connection;
  if (!connection) return "medium";

  if (connection.effectiveType === "4g") return "fast";
  if (connection.effectiveType === "2g") return "slow";
  return "medium";
};

const detectOS = (): "ios" | "android" | "windows" | "macos" | "linux" | "unknown" => {
  const userAgent = navigator.userAgent.toLowerCase();
  const platform = navigator.platform.toLowerCase();

  // iOS detection
  if (/iphone|ipod|ipad/.test(userAgent) || (platform === "macosintel" && navigator.maxTouchPoints > 1)) {
    return "ios";
  }

  // Android detection
  if (/android/.test(userAgent)) {
    return "android";
  }

  // macOS detection
  if (/mac/.test(platform)) {
    return "macos";
  }

  // Windows detection
  if (/win/.test(platform)) {
    return "windows";
  }

  // Linux detection
  if (/linux/.test(platform)) {
    return "linux";
  }

  return "unknown";
};

const detectBrowser = (): "safari" | "chrome" | "firefox" | "edge" | "unknown" => {
  const userAgent = navigator.userAgent.toLowerCase();

  // Edge detection (must come before Chrome check)
  if (/edg/.test(userAgent)) {
    return "edge";
  }

  // Chrome detection (must come before Safari check)
  if (/chrome/.test(userAgent) && !/edg/.test(userAgent)) {
    return "chrome";
  }

  // Safari detection
  if (/safari/.test(userAgent) && !/chrome/.test(userAgent) && !/edg/.test(userAgent)) {
    return "safari";
  }

  // Firefox detection
  if (/firefox/.test(userAgent)) {
    return "firefox";
  }

  return "unknown";
};

const detectPWAInstalled = (): boolean => {
  // Check display mode
  const isStandalone = window.matchMedia("(display-mode: standalone)").matches;

  // iOS-specific check
  const isIOSStandalone = (navigator as Navigator & { standalone?: boolean }).standalone === true;

  return isStandalone || isIOSStandalone;
};

const canInstallPWA = (os: DeviceInfo["os"], browser: DeviceInfo["browser"], isPWAInstalled: boolean): boolean => {
  // Already installed - can't install again
  if (isPWAInstalled) {
    return false;
  }

  // iOS only supports installation in Safari
  if (os === "ios") {
    return browser === "safari";
  }

  // Android Chrome/Edge supports PWA installation
  if (os === "android") {
    return browser === "chrome" || browser === "edge";
  }

  // Desktop Chrome/Edge supports PWA installation
  if (os === "windows" || os === "macos" || os === "linux") {
    return browser === "chrome" || browser === "edge";
  }

  return false;
};

export const DeviceProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { user } = useAuth();
  const [deviceInfo, setDeviceInfo] = useState<DeviceInfo>(() => {
    const os = detectOS();
    const browser = detectBrowser();
    const isPWAInstalled = detectPWAInstalled();

    return {
      userId: user?.uid ?? "",
      isTouchDevice: false,
      isHoverSupported: true,
      screenSize: {
        width: window.innerWidth,
        height: window.innerHeight,
      },
      orientation:
        window.innerHeight > window.innerWidth ? "portrait" : "landscape",
      deviceType: detectDeviceType(window.innerWidth),
      deviceId: generateDeviceId(),
      hasCamera: false,
      hasGPS: false,
      connection: {
        type:
          (navigator as Navigator & { connection?: NavigatorConnection })
            .connection?.effectiveType ?? "unknown",
        speed: detectConnectionSpeed(),
      },
      os,
      browser,
      isPWAInstalled,
      canInstallPWA: canInstallPWA(os, browser, isPWAInstalled),
    };
  });

  const [registeredDevices, setRegisteredDevices] = useState<DeviceInfo[]>([]);
  const [syncStatus, setSyncState] = useState<SyncState>({
    isSyncing: false,
    error: null,
    pendingUpdates: [],
    pendingUploads: 0,
    status: "unknown",
    requiresFullSync: false,
  });

  const { profile: coachProfile, athletes: coachAthletes } = useCoachProfile();
  // Device management functions
  const getRegisteredDevices = async (): Promise<DeviceInfo[]> => {
    if (!user?.uid) return [];

    try {
      const devices = await SyncService.getRegisteredDevices(user.uid);
      setRegisteredDevices(devices);
      const thisDevice = devices.find(
        (x) => x.deviceId === deviceInfo.deviceId,
      );
      if (thisDevice) {
        setDeviceInfo({ ...deviceInfo, lastSync: thisDevice.lastSync });
      }

      return devices;
    } catch (error) {
      console.error("Error fetching registered devices:", error);
      return [];
    }
  };

  const registerDevice = async () => {
    if (!user?.uid) return;

    try {
      await SyncService.registerDevice(
        user.uid,
        deviceInfo.deviceId,
        deviceInfo,
      );
      await getRegisteredDevices();
    } catch (error) {
      console.error("Error registering device:", error);
      setSyncState((prev) => ({ ...prev, error: "Failed to register device" }));
    }
  };

  const unregisterDevice = async () => {
    if (!user?.uid) return;

    try {
      await SyncService.removeDevice(user.uid, deviceInfo.deviceId);
      await getRegisteredDevices();
    } catch (error) {
      console.error("Error unregistering device:", error);
      setSyncState((prev) => ({
        ...prev,
        error: "Failed to unregister device",
      }));
    }
  };

  const removeDevice = async (deviceToRemove: string) => {
    if (!user?.uid || deviceToRemove === deviceInfo.deviceId) return;

    try {
      await SyncService.removeDevice(user.uid, deviceToRemove);
      await getRegisteredDevices();
    } catch (error) {
      console.error("Error removing device:", error);
      setSyncState((prev) => ({ ...prev, error: "Failed to remove device" }));
    }
  };

  const updateDeviceName = async (deviceId: string, deviceName: string) => {
    if (!user?.uid) return;

    try {
      // Get the device data first
      const device = await SyncService.getDeviceData(user.uid, deviceId);
      if (!device) {
        throw new Error("Device not found");
      }

      // Update with new name
      const updatedDevice = { ...device, deviceName };
      await SyncService.updateDeviceData(user.uid, deviceId, updatedDevice);

      // If this is the current device, update local state
      if (deviceId === deviceInfo.deviceId) {
        setDeviceInfo((prev) => ({ ...prev, deviceName }));
      }

      // Refresh the registered devices list
      await getRegisteredDevices();
    } catch (error) {
      console.error("Error updating device name:", error);
      setSyncState((prev) => ({
        ...prev,
        error: "Failed to update device name",
      }));
      throw error;
    }
  };

  const isCurrentDevice = (deviceId: string): boolean => {
    return deviceId === deviceInfo.deviceId;
  };

  const fetchSyncStatus = async () => {
    if (!user?.uid) return;

    setSyncState((prev) => ({ ...prev, isLoading: true }));
    try {
      // Get sync status for current device
      const status = SyncService.checkSyncStatus(deviceInfo.lastSync);
      setSyncState((prev) => ({
        ...prev,
        requiresFullSync: status.requiresFullSync,
        status: status.status,
      }));

      // If coach we need to check all atheltes information
      const idsToCheck =
        user.role === "coach" && coachProfile
          ? [user.uid, ...coachAthletes.map((x) => x.athleteId)]
          : [user.uid];

      // Get pending changes (downloads - remote changes to pull)
      // Filter out changes created by this device (those are local, not downloads)
      const allChanges: SyncEvent[] = (
        await Promise.all(
          idsToCheck.map((id) =>
            SyncService.getChangesSinceLastSync(
              id,
              deviceInfo.lastSync ?? null,
            ),
          ),
        )
      ).flat();

      // Only count changes from OTHER devices as pending downloads
      const remoteChanges = allChanges.filter(
        (event) => event.deviceId !== deviceInfo.deviceId,
      );

      // Get pending uploads (local changes in sync queue)
      const kvStatus = await getKVStatus();
      const pendingUploadsCount = kvStatus.queueSize;

      console.log(
        "pending downloads (from other devices):",
        remoteChanges.length,
        "pending uploads:",
        pendingUploadsCount,
      );
      console.log(
        "filtered out local changes:",
        allChanges.length - remoteChanges.length,
      );
      setSyncState((prev) => ({
        ...prev,
        pendingUpdates: remoteChanges,
        pendingUploads: pendingUploadsCount,
      }));

      // Fetch registered devices
      await getRegisteredDevices();
    } catch (error) {
      console.error("Error fetching sync status:", error);
      setSyncState((prev) => ({ ...prev, error: "Failed to load sync state" }));
    } finally {
      setSyncState((prev) => ({ ...prev, isLoading: false }));
    }
  };

  const sync = async () => {
    if (!user?.uid) return;

    setSyncState((prev) => ({ ...prev, isSyncing: true }));

    try {
      // First, pull any remote changes
      await pullSync();

      // Then push local changes
      const results = await SyncService.syncEvents(syncStatus.pendingUpdates);

      // Only update last sync if all syncs were successful
      if (results.every((success) => success)) {
        const now = Date.now();
        await SyncService.updateDeviceData(user.uid, deviceInfo.deviceId, {
          ...deviceInfo,
          lastSync: now,
        });
        setDeviceInfo((prev) => ({ ...prev, lastSync: now }));
      } else {
        console.warn("Some syncs failed, not updating last sync time");
      }

      // Refresh sync status
      await refreshDeviceSyncData();
    } catch (error) {
      console.error("Error during sync:", error);
      setSyncState((prev) => ({ ...prev, error: "Sync failed" }));
    } finally {
      setSyncState((prev) => ({ ...prev, isSyncing: false }));
    }
  };

  const pullSync = async () => {
    if (!user?.uid) return;

    setSyncState((prev) => ({
      ...prev,
      isSyncing: true,
      pullProgress: undefined,
    }));

    try {
      console.log("🔄 Starting pull sync...");

      // Check if full sync is required
      if (syncStatus.requiresFullSync) {
        console.log("📥 Performing full sync (device is stale)...");

        const result = await PullSyncService.performFullSync(
          user.uid,
          user.role as "coach" | "athlete",
          (progress) => {
            setSyncState((prev) => ({
              ...prev,
              pullProgress: {
                current: progress.current,
                total: progress.total,
                message: `Syncing ${progress.key}...`,
              },
            }));
          },
        );

        console.log(
          `✅ Full sync complete: ${result.syncedKeys.length}/${result.totalKeys} keys synced`,
        );
      } else {
        // Pull incremental changes
        const userIds =
          user.role === "coach" && coachProfile
            ? [user.uid, ...coachAthletes.map((a) => a.athleteId)]
            : [user.uid];

        console.log(`📥 Pulling changes for ${userIds.length} user(s)...`);

        const result = await PullSyncService.pullChangesForMultipleUsers(
          userIds,
          deviceInfo.lastSync ?? null,
          (progress) => {
            setSyncState((prev) => ({
              ...prev,
              pullProgress: {
                current: progress.current,
                total: progress.total,
                message: `Pulling changes for user ${progress.current}/${progress.total}...`,
              },
            }));
          },
        );

        console.log(
          `✅ Pull sync complete: ${result.applied}/${result.totalEvents} changes applied`,
        );
      }

      // Update last pull sync time
      const now = Date.now();
      setSyncState((prev) => ({
        ...prev,
        lastPullSync: now,
        pullProgress: undefined,
      }));

      // Update device last sync time
      await SyncService.updateDeviceData(user.uid, deviceInfo.deviceId, {
        ...deviceInfo,
        lastSync: now,
      });
      setDeviceInfo((prev) => ({ ...prev, lastSync: now }));

      // Refresh sync status
      await refreshDeviceSyncData();
    } catch (error) {
      console.error("Error during pull sync:", error);
      setSyncState((prev) => ({
        ...prev,
        error: "Pull sync failed",
        pullProgress: undefined,
      }));
    } finally {
      setSyncState((prev) => ({ ...prev, isSyncing: false }));
    }
  };

  const refreshDeviceSyncData = async () => {
    await getRegisteredDevices();
    await fetchSyncStatus();
  };

  // Initialize device and sync on mount
  useEffect(() => {
    if (user?.uid) {
      // Set userId in deviceInfo
      setDeviceInfo((prev) => ({ ...prev, userId: user.uid }));

      const initializeDevice = async () => {
        try {
          await registerDevice();
          await fetchSyncStatus();

          // Auto-pull changes on startup if online
          if (navigator.onLine) {
            console.log("🚀 Auto-pulling changes on startup...");
            await pullSync();
          } else {
            console.log("📴 Device is offline, skipping auto-pull");
          }
        } catch (error) {
          console.error("Error initializing device:", error);
          setSyncState((prev) => ({
            ...prev,
            error: "Failed to initialize device",
          }));
        }
      };

      initializeDevice();
    }
  }, [user?.uid]);

  // Device feature detection effect
  useEffect(() => {
    const detectFeatures = () => {
      const isTouchCapable =
        "ontouchstart" in window || navigator.maxTouchPoints > 0;
      const isHoverSupported = window.matchMedia("(hover: hover)").matches;
      const hasCamera = !!navigator.mediaDevices?.getUserMedia;
      const hasGPS = !!navigator.geolocation;

      // Re-detect PWA installation status (in case it changes during session)
      const os = detectOS();
      const browser = detectBrowser();
      const isPWAInstalled = detectPWAInstalled();

      setDeviceInfo((prev) => ({
        ...prev,
        isTouchDevice: isTouchCapable,
        isHoverSupported,
        hasCamera,
        hasGPS,
        os,
        browser,
        isPWAInstalled,
        canInstallPWA: canInstallPWA(os, browser, isPWAInstalled),
      }));
    };

    detectFeatures();

    const handleResize = () => {
      const width = window.innerWidth;
      const height = window.innerHeight;

      setDeviceInfo((prev) => ({
        ...prev,
        screenSize: { width, height },
        orientation: height > width ? "portrait" : "landscape",
        deviceType: detectDeviceType(width),
      }));
    };

    const handleConnectionChange = () => {
      const connection = (
        navigator as Navigator & { connection?: NavigatorConnection }
      ).connection;

      setDeviceInfo((prev) => ({
        ...prev,
        connection: {
          type: connection?.effectiveType ?? "unknown",
          speed: detectConnectionSpeed(),
        },
      }));
    };

    window.addEventListener("resize", handleResize);
    window.addEventListener("orientationchange", handleResize);

    const connection = (
      navigator as Navigator & { connection?: NavigatorConnection }
    ).connection;
    if (connection?.addEventListener) {
      connection.addEventListener("change", handleConnectionChange);
    }

    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("orientationchange", handleResize);
      if (connection?.removeEventListener) {
        connection.removeEventListener("change", handleConnectionChange);
      }
    };
  }, []);

  const contextValue: DeviceContextType = {
    ...deviceInfo,
    syncStatus,
    registerDevice,
    unregisterDevice,
    getRegisteredDevices,
    removeDevice,
    updateDeviceName,
    registeredDevices,
    isCurrentDevice,
    refreshDeviceSyncData,
    sync,
    pullSync,
  };

  return (
    <DeviceContext.Provider value={contextValue}>
      {children}
    </DeviceContext.Provider>
  );
};

export const useDevice = () => {
  const context = useContext(DeviceContext);
  if (context === undefined) {
    throw new Error("useDevice must be used within a DeviceProvider");
  }
  return context;
};

export const useOrientation = () => {
  const { orientation } = useDevice();
  return orientation;
};

export const useScreenSize = () => {
  const { screenSize } = useDevice();
  return screenSize;
};

export const useDeviceType = () => {
  const { deviceType } = useDevice();
  return deviceType;
};
